/**
 * Info client for read-only queries
 * Follows pattern from pkg/netrunner/clients for public/session auth operations
 * Automatically converts satoshi strings to decimals for display
 */

import { HttpClient } from "../../transport/http/HttpClient";
import { fromSatoshi } from "../../utils/math";
import { InfoClientConfig } from "../../types";
import {
  RawBucket,
  MarketsResponse,
  RawMarket,
  RawMarketStats,
  ExchangeInfoResponse,
  RawOrderbook,
  OrderbookEntry,
  TradesResponse,
  RawTrade,
  RawAggTrade,
  KlinesResponse,
  RawKline,
  RawPremiumIndex,
  FundingRateResponse,
  RawFundingRate,
  OpenInterestResponse,
  TWAPResponse,
  IndexInfoResponse,
  AssetIndexResponse,
  ConstituentsResponse,
  DeliveryPriceResponse,
  BasisResponse,
  LongShortRatioResponse,
  PositionsResponse,
  RawPosition,
  RawBalance,
  GetBalancesResponse,
  OrdersResponse,
  RawOrder,
  MarketDataResponse,
  MarketCandlesResponse,
  NonceStatusResponse,
  SupportedActionsResponse,
  ValidateActionResponse,
  HealthSummaryResponse,
  ServerTimeResponse,
  TPSMetricsResponse,
  LatencyMetricsResponse,
} from "../../types/api";

export class InfoClient {
  private httpClient: HttpClient;
  private sessionToken?: string;

  constructor(config: InfoClientConfig) {
    this.httpClient = new HttpClient(config.baseURL);
    this.sessionToken = config.sessionToken;
  }

  /**
   * Set session token for authenticated requests
   */
  setSessionToken(token: string): void {
    this.sessionToken = token;
  }

  /**
   * Get headers for requests
   * Follows pattern from DexClient.Do() for public/session auth
   */
  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: "application/json, */*",
    };

    if (this.sessionToken) {
      headers["Authorization"] = `Bearer ${this.sessionToken}`;
    }

    return headers;
  }

  // ============================================================================
  // MARKET DATA ENDPOINTS (Public)
  // ============================================================================

  /**
   * Get all markets
   * Uses HTTP endpoint /market/v1/all (matches Go client GetMarkets)
   */
  async getMarkets(): Promise<RawMarket[]> {
    const response = await this.httpClient.get(
      "/market/v1/all",
      this.getHeaders(),
    );
    const body = response.body as MarketsResponse;
    if (Array.isArray(body)) {
      return body;
    }
    if (body && typeof body === "object" && "markets" in body) {
      return body.markets;
    }
    return [];
  }

  /**
   * Get markets with filters
   * Uses HTTP endpoint /market/v1/all (matches Go client GetMarketsWithFilters)
   */
  async getMarketsWithFilters(
    filter?: string,
    activeOnly?: boolean,
  ): Promise<RawMarket[]> {
    let path = "/market/v1/all";
    const params: string[] = [];
    if (filter) {
      params.push(`filter=${filter}`);
    }
    if (activeOnly) {
      params.push("active_only=true");
    }
    if (params.length > 0) {
      path += "?" + params.join("&");
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body as MarketsResponse;
    if (Array.isArray(body)) {
      return body;
    }
    if (body && typeof body === "object" && "markets" in body) {
      return body.markets;
    }
    return [];
  }

  /**
   * Get market by ID
   * Uses HTTP endpoint /market/v1/market/{marketId} (matches Go client GetMarketByID)
   */
  async getMarketByID(marketId: string): Promise<RawMarket> {
    const response = await this.httpClient.get(
      `/market/v1/market/${marketId}`,
      this.getHeaders(),
    );
    return response.body as RawMarket;
  }

  /**
   * Get market by moniker symbol
   * Uses HTTP endpoint /market/v1/market/by-moniker/{moniker} (matches Go client GetMarketByMoniker)
   */
  async getMarketByMoniker(moniker: string): Promise<RawMarket> {
    const response = await this.httpClient.get(
      `/market/v1/market/by-moniker/${moniker}`,
      this.getHeaders(),
    );
    return response.body as RawMarket;
  }

  /**
   * Get market statistics (ticker data)
   * Uses HTTP endpoint /market/v1/market/{marketId}/stats (matches Go client GetMarketStats)
   */
  async getMarketStats(marketId: string): Promise<RawMarketStats> {
    const response = await this.httpClient.get(
      `/market/v1/market/${marketId}/stats`,
      this.getHeaders(),
    );
    return response.body as RawMarketStats;
  }

  /**
   * Get market ticker (alias for getMarketStats)
   * @deprecated Use getMarketStats instead
   */
  async getTicker(marketId: string): Promise<RawMarketStats> {
    return this.getMarketStats(marketId);
  }

  /**
   * Get exchange info
   * Uses HTTP endpoint /market/v1/exchangeInfo (matches Go client GetExchangeInfo)
   */
  async getExchangeInfo(): Promise<ExchangeInfoResponse> {
    const response = await this.httpClient.get(
      "/market/v1/exchangeInfo",
      this.getHeaders(),
    );
    return response.body as ExchangeInfoResponse;
  }

  /**
   * Get orderbook for a market
   * Uses HTTP endpoint /clob/v1/orderbook/{marketId} (matches Go client GetOrderbook)
   */
  async getOrderbook(
    marketId: string,
    convertToDecimal: boolean = true,
    depth: number = 20,
  ): Promise<RawOrderbook> {
    let path = `/clob/v1/orderbook/${marketId}`;
    let depthValue = 20;
    if (depth > 0 && depth < 100) {
      depthValue = depth;
    }
    path += `?depth=${depthValue}`;

    const response = await this.httpClient.get(path, this.getHeaders());
    const orderbook = response.body as RawOrderbook;

    if (convertToDecimal && orderbook) {
      if (orderbook.bids && Array.isArray(orderbook.bids)) {
        orderbook.bids = (orderbook.bids as [string, string][]).map(
          ([price, size]) => [
            fromSatoshi(price),
            fromSatoshi(size),
          ],
        );
      }
      if (orderbook.asks && Array.isArray(orderbook.asks)) {
        orderbook.asks = (orderbook.asks as [string, string][]).map(
          ([price, size]) => [
            fromSatoshi(price),
            fromSatoshi(size),
          ],
        );
      }
    }

    return orderbook;
  }

  /**
   * Get market trades by market ID
   * Uses HTTP endpoint /clob/v1/trades?market_id={marketId} (matches Go client GetMarketTrades)
   * Response format: { trades: RawTrade[], total_count: number, market_id: string, symbol: string, timestamp: string }
   */
  async getMarketTrades(marketId: string, limit?: number): Promise<RawTrade[]> {
    let path = `/clob/v1/trades?market_id=${marketId}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    console.debug(
      "[InfoClient] getMarketTrades response for marketId:",
      marketId,
      "response:",
      response.body,
    );

    const body = response.body as TradesResponse;

    // Handle the actual API response structure
    if (
      body &&
      typeof body === "object" &&
      "trades" in body &&
      Array.isArray(body.trades)
    ) {
      return body.trades;
    }

    // Fallback for legacy formats (direct array or { data: [...] })
    if (Array.isArray(body)) {
      return body;
    }
    // Check for legacy format with 'data' property (not part of TradesResponse type)
    const bodyAny = body as any;
    if (bodyAny?.data && Array.isArray(bodyAny.data)) {
      return bodyAny.data;
    }

    console.warn(
      "[InfoClient] getMarketTrades: Unexpected response structure:",
      response.body,
    );
    // Return empty array if response structure is unexpected
    return [];
  }

  /**
   * Get recent trades for a market (by market ID)
   * Uses HTTP endpoint /clob/v1/trades?market_id={marketId} (matches Go client GetMarketTrades)
   */
  async getRecentTrades(
    marketId: string,
    limit: number = 100,
  ): Promise<RawTrade[]> {
    return this.getMarketTrades(marketId, limit);
  }

  /**
   * Get trades by symbol
   * Uses HTTP endpoint /clob/v1/trades?symbol={symbol} (matches Go client GetTrades)
   * Response format: { trades: RawTrade[], total_count: number, market_id: string, symbol: string, timestamp: string }
   */
  async getTrades(symbol: string, limit?: number): Promise<RawTrade[]> {
    let path = `/clob/v1/trades?symbol=${symbol}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body as TradesResponse;

    // Handle the actual API response structure
    if (
      body &&
      typeof body === "object" &&
      "trades" in body &&
      Array.isArray(body.trades)
    ) {
      return body.trades;
    }

    // Fallback for legacy formats (direct array or { data: [...] })
    if (Array.isArray(body)) {
      return body;
    }
    // Check for legacy format with 'data' property (not part of TradesResponse type)
    const bodyAny = body as any;
    if (bodyAny?.data && Array.isArray(bodyAny.data)) {
      return bodyAny.data;
    }

    return [];
  }

  /**
   * Get aggregate trades
   * Uses HTTP endpoint /market/v1/aggTrades (matches Go client GetAggTrades)
   */
  async getAggTrades(
    symbol: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<RawAggTrade[]> {
    let path = `/market/v1/aggTrades?symbol=${symbol}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    if (startTime && startTime > 0) {
      path += `&startTime=${startTime}`;
    }
    if (endTime && endTime > 0) {
      path += `&endTime=${endTime}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get klines (candlestick data)
   * Uses HTTP endpoint /clob/v1/klines (matches Go client GetKlines)
   */
  async getKlines(
    symbol: string,
    interval: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<RawKline[]> {
    let path = `/clob/v1/klines?symbol=${symbol}&interval=${interval}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    if (startTime && startTime > 0) {
      path += `&startTime=${startTime}`;
    }
    if (endTime && endTime > 0) {
      path += `&endTime=${endTime}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body as KlinesResponse;
    return Array.isArray(body) ? body : [];
  }

  /**
   * Get continuous klines
   * Uses HTTP endpoint /market/v1/continuousKlines (matches Go client GetContinuousKlines)
   */
  async getContinuousKlines(
    pair: string,
    contractType: string,
    interval: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<RawKline[]> {
    let path = `/market/v1/continuousKlines?pair=${pair}&contractType=${contractType}&interval=${interval}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    if (startTime && startTime > 0) {
      path += `&startTime=${startTime}`;
    }
    if (endTime && endTime > 0) {
      path += `&endTime=${endTime}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get premium index (mark price and funding rate)
   * Uses HTTP endpoint /market/v1/premiumIndex (matches Go client GetPremiumIndex)
   */
  async getPremiumIndex(
    symbol: string,
  ): Promise<RawPremiumIndex | RawPremiumIndex[]> {
    let path = "/market/v1/premiumIndex";
    if (symbol) {
      path += `?symbol=${symbol}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body;
    return Array.isArray(body) ? body : (body as RawPremiumIndex);
  }

  /**
   * Get funding rate history
   * Uses HTTP endpoint /market/v1/fundingRate (matches Go client GetFundingRate)
   */
  async getFundingRate(
    symbol: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<RawFundingRate[]> {
    let path = "/market/v1/fundingRate";
    const params: string[] = [];
    if (symbol) {
      params.push(`symbol=${symbol}`);
    }
    if (limit && limit > 0) {
      params.push(`limit=${limit}`);
    }
    if (startTime && startTime > 0) {
      params.push(`startTime=${startTime}`);
    }
    if (endTime && endTime > 0) {
      params.push(`endTime=${endTime}`);
    }
    if (params.length > 0) {
      path += "?" + params.join("&");
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body as FundingRateResponse;
    if (Array.isArray(body)) {
      return body;
    }
    if (
      body &&
      typeof body === "object" &&
      "data" in body &&
      Array.isArray(body.data)
    ) {
      return body.data;
    }
    return body ? [body as RawFundingRate] : [];
  }

  /**
   * Get funding info
   * Uses HTTP endpoint /market/v1/fundingInfo (matches Go client GetFundingInfo)
   */
  async getFundingInfo(): Promise<RawFundingRate[]> {
    const response = await this.httpClient.get(
      "/market/v1/fundingInfo",
      this.getHeaders(),
    );
    const body = response.body;
    return Array.isArray(body) ? body : [];
  }

  /**
   * Get delivery price
   * Uses HTTP endpoint /market/v1/deliveryPrice (matches Go client GetDeliveryPrice)
   */
  async getDeliveryPrice(symbol: string): Promise<DeliveryPriceResponse> {
    const response = await this.httpClient.get(
      `/market/v1/deliveryPrice?pair=${symbol}`,
      this.getHeaders(),
    );
    return response.body as DeliveryPriceResponse;
  }

  /**
   * Get open interest
   * Uses HTTP endpoint /market/v1/openInterest (matches Go client GetOpenInterest)
   */
  async getOpenInterest(symbol: string): Promise<OpenInterestResponse> {
    const response = await this.httpClient.get(
      `/market/v1/openInterest?symbol=${symbol}`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get top long/short position ratio
   * Uses HTTP endpoint /market/v1/topLongShortPositionRatio (matches Go client GetTopLongShortPositionRatio)
   */
  async getTopLongShortPositionRatio(
    symbol: string,
    period: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<LongShortRatioResponse | LongShortRatioResponse[]> {
    let path = `/market/v1/topLongShortPositionRatio?symbol=${symbol}&period=${period}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    if (startTime && startTime > 0) {
      path += `&startTime=${startTime}`;
    }
    if (endTime && endTime > 0) {
      path += `&endTime=${endTime}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body;
    return Array.isArray(body) ? body : (body as LongShortRatioResponse);
  }

  /**
   * Get global long/short account ratio
   * Uses HTTP endpoint /market/v1/globalLongShortAccountRatio (matches Go client GetGlobalLongShortAccountRatio)
   */
  async getGlobalLongShortAccountRatio(
    symbol: string,
    period: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<LongShortRatioResponse | LongShortRatioResponse[]> {
    let path = `/market/v1/globalLongShortAccountRatio?symbol=${symbol}&period=${period}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    if (startTime && startTime > 0) {
      path += `&startTime=${startTime}`;
    }
    if (endTime && endTime > 0) {
      path += `&endTime=${endTime}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body;
    return Array.isArray(body) ? body : (body as LongShortRatioResponse);
  }

  /**
   * Get 24hr ticker statistics by symbol
   * Uses HTTP endpoint /market/v1/moniker/24hr (matches Go client GetTicker24hr)
   */
  async getTicker24hr(
    symbol: string,
  ): Promise<RawMarketStats | RawMarketStats[]> {
    let path = "/market/v1/moniker/24hr";
    if (symbol) {
      path += `?symbol=${symbol}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body;
    return Array.isArray(body) ? body : (body as RawMarketStats);
  }

  /**
   * Get ticker price by symbol
   * Uses HTTP endpoint /market/v1/moniker/price (matches Go client GetTickerPrice)
   */
  async getTickerPrice(
    symbol: string,
  ): Promise<RawMarketStats | RawMarketStats[]> {
    let path = "/market/v1/moniker/price";
    if (symbol) {
      path += `?symbol=${symbol}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body;
    return Array.isArray(body) ? body : (body as RawMarketStats);
  }

  /**
   * Get orderbook ticker by symbol
   * Uses HTTP endpoint /market/v1/moniker/bookTicker (matches Go client GetOrderbookMoniker)
   */
  async getOrderbookMoniker(
    symbol: string,
  ): Promise<RawOrderbook | RawOrderbook[]> {
    let path = "/market/v1/moniker/bookTicker";
    if (symbol) {
      path += `?symbol=${symbol}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body;
    return Array.isArray(body) ? body : (body as RawOrderbook);
  }

  /**
   * Get basis data
   * Uses HTTP endpoint /market/v1/basis (matches Go client GetBasis)
   */
  async getBasis(
    symbol: string,
    period: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<BasisResponse | BasisResponse[]> {
    let path = `/market/v1/basis?symbol=${symbol}&period=${period}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    if (startTime && startTime > 0) {
      path += `&startTime=${startTime}`;
    }
    if (endTime && endTime > 0) {
      path += `&endTime=${endTime}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body;
    return Array.isArray(body) ? body : (body as BasisResponse);
  }

  /**
   * Get index info
   * Uses HTTP endpoint /market/v1/indexInfo (matches Go client GetIndexInfo)
   */
  async getIndexInfo(
    symbol: string,
  ): Promise<IndexInfoResponse | IndexInfoResponse[]> {
    let path = "/market/v1/indexInfo";
    if (symbol) {
      path += `?symbol=${symbol}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get asset index
   * Uses HTTP endpoint /market/v1/assetIndex (matches Go client GetAssetIndex)
   */
  async getAssetIndex(
    symbol: string,
  ): Promise<AssetIndexResponse | AssetIndexResponse[]> {
    let path = "/market/v1/assetIndex";
    if (symbol) {
      path += `?symbol=${symbol}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body;
    return Array.isArray(body) ? body : (body as AssetIndexResponse);
  }

  /**
   * Get constituents
   * Uses HTTP endpoint /market/v1/constituents (matches Go client GetConstituents)
   */
  async getConstituents(symbol: string): Promise<ConstituentsResponse> {
    const response = await this.httpClient.get(
      `/market/v1/constituents?symbol=${symbol}`,
      this.getHeaders(),
    );
    return response.body as ConstituentsResponse;
  }

  /**
   * Get TWAP (time-weighted average price)
   * Uses HTTP endpoint /market/v1/twap (matches Go client GetTWAP)
   */
  async getTWAP(
    symbol: string,
    durationMinutes: number,
  ): Promise<TWAPResponse> {
    const response = await this.httpClient.get(
      `/market/v1/twap?symbol=${symbol}&duration=${durationMinutes}`,
      this.getHeaders(),
    );
    return response.body as TWAPResponse;
  }

  // ============================================================================
  // USER DATA ENDPOINTS (Session Auth Required)
  // ============================================================================

  /**
   * Get positions for authenticated user
   * Uses HTTP endpoint /bucket/v1/address/{address}/positions (matches Go client GetUserPositions)
   * Note: Requires walletAddress to be set or passed
   * 
   * Actual API response: Direct array of RawPosition objects
   * Also handles wrapped format { positions: [...] } for backward compatibility
   */
  async getPositions(
    walletAddress: string,
    activeOnly?: boolean,
  ): Promise<RawPosition[]> {
    if (!walletAddress) {
      throw new Error("walletAddress is required for getPositions");
    }
    let path = `/bucket/v1/address/${walletAddress}/positions`;
    if (activeOnly !== undefined) {
      path += `?active_only=${activeOnly}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body as PositionsResponse | RawPosition[];
    // Normalize response to always return an array
    // Handle cases where API returns direct array (actual format) or wrapped { positions: [...] }
    if (Array.isArray(body)) {
      return body;
    }
    if (
      body &&
      typeof body === "object" &&
      "positions" in body &&
      Array.isArray(body.positions)
    ) {
      return body.positions;
    }
    // Return empty array if response structure is unexpected
    return [];
  }

  /**
   * Get balance for an address
   * Uses HTTP endpoint /bank/v1/balance/{address} (matches Go client GetBalance)
   */
  async getBalance(address: string, assetID: string): Promise<RawBalance> {
    let path = `/bank/v1/balance/${address}?asset=${assetID}`;
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get balances for an address
   * Uses HTTP endpoint /bank/v1/balances/{address} (matches Go client GetBalances)
   *
   * Actual API response structure:
   * {
   *   "address": "0x...",
   *   "balances": [
   *     {
   *       "asset": "MORM",
   *       "balance": "100000000000000000000000",
   *       "available_balance": "100000000000000000000000",
   *       "locked_balance": "0"
   *     },
   *     ...
   *   ],
   *   "shard_id": ""
   * }
   */
  async getBalances(address: string): Promise<RawBalance[]> {
    const response = await this.httpClient.get(
      `/bank/v1/balances/${address}`,
      this.getHeaders(),
    );
    const body = response.body as GetBalancesResponse;

    // Handle the actual API response structure (GetBalancesResponse)
    if (
      body &&
      typeof body === "object" &&
      "balances" in body &&
      Array.isArray(body.balances)
    ) {
      return body.balances;
    }

    return [];
  }

  /**
   * Get balance with decimal conversion
   */
  async getBalanceDecimal(
    address: string,
    assetID: string,
  ): Promise<RawBalance> {
    const balance = await this.getBalance(address, assetID);

    if (balance && typeof balance === "object") {
      const converted: RawBalance & Record<string, any> = { ...balance };
      if (converted.balance) {
        converted.balance = fromSatoshi(converted.balance) as any;
      }
      // Convert available_balance (snake_case from API) to available (camelCase)
      if (converted.available_balance) {
        converted.available = fromSatoshi(converted.available_balance);
        // Keep original field for backward compatibility
        converted.available_balance = fromSatoshi(converted.available_balance) as any;
      }
      // Also check for available (in case API returns camelCase)
      if (converted.available && typeof converted.available === "string") {
        converted.available = fromSatoshi(converted.available);
      }
      if (converted.locked_balance) {
        converted.locked = fromSatoshi(converted.locked_balance);
        // Keep original field for backward compatibility
        converted.locked_balance = fromSatoshi(converted.locked_balance) as any;
      }
      // Also check for locked (in case API returns camelCase)
      if (converted.locked && typeof converted.locked === "string") {
        converted.locked = fromSatoshi(converted.locked);
      }
      // Type assertion: converted has all RawBalance properties (spread from balance)
      // even though some values are converted from string to number
      return converted as RawBalance;
    }

    return balance;
  }

  /**
   * Get orders for an address
   * Uses HTTP endpoint /clob/v1/address/orders (matches Go client GetOrders)
   * @param address - Wallet address to get orders for
   * @param assetPair - Optional asset pair ID to filter orders
   * @returns Response object with structure: { orders: Order[], total_count: number, timestamp: string }
   * @example
   * // Get all orders for an address
   * const result = await client.getOrders('0x3061750d3dF69ef7B8d4407CB7f3F879Fd9d2398');
   * // result.orders contains the array of orders
   * // result.total_count contains the total count
   *
   * // Get orders filtered by asset pair
   * const result = await client.getOrders('0x3061750d3dF69ef7B8d4407CB7f3F879Fd9d2398', '16210999351379558260');
   */
  async getOrders(
    address: string,
    assetPair?: string,
  ): Promise<OrdersResponse> {
    let path = `/clob/v1/address/orders?address=${encodeURIComponent(address)}`;
    if (assetPair) {
      path += `&asset_pair=${encodeURIComponent(assetPair)}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get order by ID
   * Uses HTTP endpoint /clob/v1/order/{orderID} (matches Go client GetOrder)
   */
  async getOrder(orderID: string): Promise<RawOrder> {
    const response = await this.httpClient.get(
      `/clob/v1/order/${orderID}`,
      this.getHeaders(),
    );
    return response.body as RawOrder;
  }

  /**
   * Get order history for an address
   * Uses HTTP endpoint /clob/v1/address/orders/history (matches Go client GetOrderHistory)
   */
  async getOrderHistory(address: string): Promise<OrdersResponse> {
    const response = await this.httpClient.get(
      `/clob/v1/address/orders/history?address=${address}`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get user trades for an address
   * Uses HTTP endpoint /clob/v1/address/trades (matches Go client GetUserTrades)
   * Response format: { trades: RawTrade[], total_count: number, market_id: string, symbol: string, timestamp: string }
   */
  async getUserTrades(
    address: string,
    assetPair?: string,
  ): Promise<RawTrade[]> {
    let path = `/clob/v1/address/trades?address=${address}`;
    if (assetPair) {
      path += `&asset_pair=${assetPair}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body as TradesResponse;

    // Handle the actual API response structure
    if (
      body &&
      typeof body === "object" &&
      "trades" in body &&
      Array.isArray(body.trades)
    ) {
      return body.trades;
    }

    // Fallback for legacy formats (direct array or { data: [...] })
    if (Array.isArray(body)) {
      return body;
    }
    // Check for legacy format with 'data' property (not part of TradesResponse type)
    const bodyAny = body as any;
    if (bodyAny?.data && Array.isArray(bodyAny.data)) {
      return bodyAny.data;
    }

    return [];
  }

  /**
   * Get trade history (alias for getUserTrades)
   */
  async getTradeHistory(
    address: string,
    assetPair?: string,
  ): Promise<RawTrade[]> {
    return this.getUserTrades(address, assetPair);
  }

  /**
   * Get position for an address
   * Uses HTTP endpoint /clob/v1/address/position (matches Go client GetPosition)
   * Response format: { success: boolean, error_message: string, positions: RawPosition[], total_count: number, filtered_count: number, was_filtered: boolean, shard_id: string }
   */
  async getPosition(
    address: string,
    assetPair?: string,
  ): Promise<RawPosition[]> {
    let path = `/clob/v1/address/position?address=${address}`;
    if (assetPair) {
      path += `&asset_pair=${assetPair}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body as PositionsResponse;

    // Handle the actual API response structure
    if (
      body &&
      typeof body === "object" &&
      "positions" in body &&
      Array.isArray(body.positions)
    ) {
      return body.positions;
    }

    // Return empty array if response structure is unexpected
    return [];
  }

  /**
   * Get buckets for a wallet address
   * Uses HTTP endpoint /bucket/v1/address/{address}/buckets (matches Go client GetBuckets)
   * Response format: { success: boolean, message: string, buckets: [...], total_buckets: number }
   * @returns Array of RawBucket objects (raw API format with snake_case fields and satoshi strings)
   */
  async getBuckets(walletAddress: string): Promise<RawBucket[]> {
    const response = await this.httpClient.get(
      `/bucket/v1/address/${walletAddress}/buckets`,
      this.getHeaders(),
    );
    // Normalize response to always return an array
    // Handle cases where API returns:
    // - { success: true, message: "...", buckets: [...], total_buckets: number } (new format)
    // - { buckets: [...] } (old format)
    // - Direct array (legacy format)
    if (response.body?.buckets && Array.isArray(response.body.buckets)) {
      return response.body.buckets as RawBucket[];
    }
    // Return empty array if response structure is unexpected
    return [];
  }

  /**
   * Get bucket by ID
   * Uses HTTP endpoint /bucket/v1/bucket/{bucket_id} (matches Go client GetBucket)
   */
  async getBucket(bucketId: string): Promise<RawBucket> {
    const response = await this.httpClient.get(
      `/bucket/v1/bucket/${bucketId}`,
      this.getHeaders(),
    );
    return response.body as RawBucket;
  }

  /**
   * Get market data for asset pair
   * Uses HTTP endpoint /clob/v1/market/data (matches Go client GetMarketData)
   */
  async getMarketData(assetPair: string): Promise<MarketDataResponse> {
    const response = await this.httpClient.get(
      `/clob/v1/market/data?asset_pair=${assetPair}`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get market candles
   * Uses HTTP endpoint /clob/v1/market/candles (matches Go client GetMarketCandles)
   */
  async getMarketCandles(
    assetPair: string,
    interval: string,
    limit: number,
  ): Promise<MarketCandlesResponse> {
    const response = await this.httpClient.get(
      `/clob/v1/market/candles?asset_pair=${assetPair}&interval=${interval}&limit=${limit}`,
      this.getHeaders(),
    );
    const body = response.body;
    return Array.isArray(body) ? body : [];
  }

  /**
   * Get nonce status for an address
   * Uses HTTP endpoint /router/v1/nonce/status/{address} (matches Go client GetNonceStatus)
   * @param address - Wallet address to query nonce for
   * @param reserve - Whether to reserve the nonce (default: false)
   * @returns Response with next_nonce field
   */
  async getNonceStatus(
    address: string,
    reserve: boolean = false,
  ): Promise<NonceStatusResponse> {
    let path = `/router/v1/nonce/status/${address}`;
    if (reserve) {
      path += "?reserve=true";
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  // ============================================================================
  // ROUTER V1 ENDPOINTS (Unified Write Service & Health)
  // ============================================================================

  /**
   * Get supported action types
   * Uses HTTP endpoint /router/v1/tx/supported-actions
   * @returns Array of supported action type strings
   */
  async getSupportedActions(): Promise<SupportedActionsResponse> {
    const response = await this.httpClient.get(
      "/router/v1/tx/supported-actions",
      this.getHeaders(),
    );
    return response.body as SupportedActionsResponse;
  }

  /**
   * Validate action before submission
   * Uses HTTP endpoint /router/v1/tx/validate-action
   * @param action - Action type to validate (e.g., "clob::submit_order")
   * @returns Validation result with valid flag
   */
  async validateAction(action: string): Promise<ValidateActionResponse> {
    const response = await this.httpClient.get(
      `/router/v1/tx/validate-action?action=${encodeURIComponent(action)}`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get aggregated system health overview
   * Uses HTTP endpoint /router/v1/health/summary
   * @returns System health summary with overall status and component health
   */
  async getHealthSummary(): Promise<HealthSummaryResponse> {
    const response = await this.httpClient.get(
      "/router/v1/health/summary",
      this.getHeaders(),
    );
    return response.body as HealthSummaryResponse;
  }

  /**
   * Get detailed daemon status (relayer/mailbox)
   * Uses HTTP endpoint /router/v1/health/daemons
   * @returns Detailed daemon status information
   */
  async getHealthDaemons(): Promise<HealthSummaryResponse> {
    const response = await this.httpClient.get(
      "/router/v1/health/daemons",
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get risk engine health status
   * Uses HTTP endpoint /router/v1/health/bucket
   * @returns Risk engine (bucket) health status
   */
  async getHealthBucket(): Promise<HealthSummaryResponse> {
    const response = await this.httpClient.get(
      "/router/v1/health/bucket",
      this.getHeaders(),
    );
    return response.body as HealthSummaryResponse;
  }

  /**
   * Get server time in milliseconds (Binance-compatible)
   * Uses HTTP endpoint /router/v1/time
   * @returns Server time in milliseconds (Unix timestamp)
   */
  async getServerTime(): Promise<ServerTimeResponse> {
    const response = await this.httpClient.get(
      "/router/v1/time",
      this.getHeaders(),
    );
    return response.body as ServerTimeResponse;
  }

  /**
   * Get transactions per second metrics
   * Uses HTTP endpoint /router/v1/metrics/tps
   * @returns TPS statistics including current, peak, and averages
   */
  async getMetricsTPS(): Promise<TPSMetricsResponse> {
    const response = await this.httpClient.get(
      "/router/v1/metrics/tps",
      this.getHeaders(),
    );
    return response.body as TPSMetricsResponse;
  }

  /**
   * Get latency statistics (P50, P95, P99, Avg, Max, Min)
   * Uses HTTP endpoint /router/v1/metrics/latency
   * @returns Latency percentiles and statistics
   */
  async getMetricsLatency(): Promise<LatencyMetricsResponse> {
    const response = await this.httpClient.get(
      "/router/v1/metrics/latency",
      this.getHeaders(),
    );
    return response.body as LatencyMetricsResponse;
  }
}
